import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useEntidad, useManagedContext } from "../../components/hooks"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest } from "../../utils/apiweb"
import { APIS } from "../../config/apis"
import { SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { onRequestError, onRequestNoSuccess } from "../../utils/requests"
import { getDateToString, getFormatNumber, truncateTime } from "../../utils/convert"
import DevolucionesGridModal from "../../components/controls/DevolucionesGridModal"

const initItem = {
    id: 0,
    importeTotal: 0
}

const DevolucionHistorialView = () => {
    const { setIsLoading, showMessageModal } = useManagedContext()
    const location = useLocation()

    const searchRef = useRef()

    const [lotes, setLotes] = useState([])
    const [editItem, setEditItem] = useState(initItem)
    const [showDetalle, setShowDetalle] = useState(false)

    const [, getRowEntidad ] = useEntidad({
        entidades: ['Recaudadora'],
        onLoaded: (_, isSuccess, error) => {
          if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
          key: 'Recaudadora',
          timeout: 0
        }
    })

    useEffect(() => SearchLotes(), [])

    const filterData = data => {
        const { numeroLote, fechaLote, fechaProcesoDesde, fechaProcesoHasta } = searchRef.current.filters

        let filtered = [...data]

        if (numeroLote !== '') filtered = filtered.filter(item => item.numeroLote.indexOf(numeroLote) >= 0)
        if (fechaLote) filtered = filtered.filter(item => truncateTime(item.fechaLote).getTime() === truncateTime(fechaLote).getTime())
        if (fechaProcesoDesde) filtered = filtered.filter(item => truncateTime(item.fechaProceso).getTime() >= truncateTime(fechaProcesoDesde).getTime())
        if (fechaProcesoHasta) filtered = filtered.filter(item => truncateTime(item.fechaProceso).getTime() <= truncateTime(fechaProcesoHasta).getTime())

        return filtered
    }

    const SearchLotes = () => {
        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.DEVOLUCION_LOTE,
            null,
            null,
            res => res.json().then(data => {
                data.forEach(row => {
                    if (row.fechaLote) row.fechaLote = new Date(row.fechaLote);
                    if (row.fechaProceso) row.fechaProceso = new Date(row.fechaProceso);
                    if (row.fechaControl) row.fechaControl = new Date(row.fechaControl);
                    if (row.fechaConciliacion) row.fechaConciliacion = new Date(row.fechaConciliacion);
                    if (row.fechaAcreditacion) row.fechaAcreditacion = new Date(row.fechaAcreditacion);
                    if (row.fechaRendicionIngresosPublicos) row.fechaRendicionIngresosPublicos = new Date(row.fechaRendicionIngresosPublicos);
                    if (row.fechaRendicionContaduria) row.fechaRendicionContaduria = new Date(row.fechaRendicionContaduria);
                })
                data.sort((a,b) => b.id - a.id)
                setLotes(filterData(data))
                setIsLoading(false)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const confirmRemoveId = (id) => {
        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.DEVOLUCION_LOTE,
            `/${id}`,
            null,
            () => {
                setLotes(prev => prev.filter(x => x.id !== id))
                setIsLoading(false)
            },
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const avmr = {
        onView: (item) => {
            setEditItem(item)
            setShowDetalle(true)
        },
        onRemove: ({ id }) => {
            showMessageModal({
                title: "Confirmación",
                message: "¿Está seguro de borrar el lote?",
                onConfirm: () => confirmRemoveId(id),
            })
        }
    }


    const onModalCancel = () => {
        setShowDetalle(false)
        setEditItem(initItem)
    }


    return <>
        <SectionHeading titles={[{ title: 'Devoluciones' }, { title: 'Historial de devoluciones' }]} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'N° lote', field: 'numeroLote', type: 'string', size: 4, searchOnEnter: true, presetValue: location.state?.numeroLote },
                    { title: 'Fecha lote', field: 'fechaLote', type: 'date', size: 4 },
                    { title: 'Fecha proceso desde', field: 'fechaProcesoDesde', type: 'date', size: 4 },
                    { title: 'Fecha proceso hasta', field: 'fechaProcesoHasta', type: 'date', size: 4, minValueField: 'fechaProcesoDesde' },
                ]}
                onSearch={SearchLotes}
            />

            <TableCustom
                className={'TableCustomBase m-top-20'}
                data={lotes}
                avmr={avmr}
                columns={[
                    { Header: 'Recaudadora', accessor: 'idRecaudadora', width: '14%', Cell: ({ value }) =>  getRowEntidad('Recaudadora', value)?.nombre ?? '' },
                    { Header: 'N° lote', accessor: 'numeroLote', width: '8%' },
                    { Header: 'Fecha lote', accessor: 'fechaLote', Cell: (data) => getDateToString(data.value), width: '8%' },
                    { Header: 'Fecha proceso', accessor: 'fechaProceso', Cell: (data) => getDateToString(data.value, true), width: '11%' },
                    { Header: 'Fecha control', accessor: 'fechaControl', Cell: (data) => getDateToString(data.value, true), width: '11%' },
                    { Header: 'Fecha conciliación', accessor: 'fechaConciliacion', Cell: (data) => getDateToString(data.value, true), width: '11%' },
                    { Header: 'Fecha Rend. IIPP', accessor: 'fechaRendicionIngresosPublicos', Cell: (data) => getDateToString(data.value, true), width: '11%' },
                    { Header: 'Fecha Rend. Contaduría', accessor: 'fechaRendicionContaduria', Cell: (data) => getDateToString(data.value, true), width: '11%' },
                    { Header: 'Casos', accessor: 'casos', Cell: (data) => getFormatNumber(data.value, 0), width: '5%', alignCell: 'right' },
                    { Header: 'Importe total', accessor: 'importeTotal', Cell: (data) => getFormatNumber(data.value, 2), width: '10%', alignCell: 'right' },
                ]}
            />

            {showDetalle && (
                <DevolucionesGridModal
                    lote={editItem}
                    onCancel={onModalCancel}
                />
            )}

        </section>
    </>
}

export default DevolucionHistorialView
